import { postItemVariant } from '@/lib/api/inventory.js';
import { formatCurrency } from '@/lib/format.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError, toastSuccess } from '@/lib/toast.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormEvent, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

type VariantRow = {
  id?: unknown;
  variantItemId?: unknown;
  variantName?: unknown;
  name?: unknown;
  attributes?: unknown;
  variantAttributes?: unknown;
  retailPrice?: unknown;
};

type Props = {
  itemId: string;
  hasVariants: boolean;
  variants: VariantRow[];
};

export function VariantsSection({ itemId, hasVariants, variants }: Props): ReactElement {
  const queryClient = useQueryClient();
  const [variantName, setVariantName] = useState('');
  const [variantAttrKey, setVariantAttrKey] = useState('Color');
  const [variantAttrValue, setVariantAttrValue] = useState('');
  const [variantPrice, setVariantPrice] = useState('');
  const [variantSkuSuffix, setVariantSkuSuffix] = useState('');

  const addVariantMutation = useMutation({
    mutationFn: (body: {
      name: string;
      attributes?: Record<string, string>;
      retailPrice?: number;
      skuSuffix?: string;
    }) => {
      if (!itemId) throw new Error('Item id is required');
      return postItemVariant(itemId, body);
    },
    onSuccess: () => {
      toastSuccess('Variant created');
      setVariantName('');
      setVariantAttrValue('');
      setVariantPrice('');
      setVariantSkuSuffix('');
      void queryClient.invalidateQueries({ queryKey: ['inventory-item', itemId] });
    },
    onError: (e) => toastError(parseApiError(e).message),
  });

  return (
    <section id="section-variants" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold text-slate-950">Variants</h2>
        {hasVariants || variants.length > 0 ? (
          <span className="rounded-full bg-sky-50 px-2.5 py-0.5 text-xs font-semibold text-sky-800">
            Has variants
          </span>
        ) : null}
      </div>
      <p className="mb-3 text-sm text-slate-500">
        Create child variants (color, size, capacity, etc.) from this parent item. Each variant gets
        its own stock and barcode.
      </p>
      {variants.length === 0 ? (
        <p className="mb-3 text-sm text-slate-500">No variants yet.</p>
      ) : (
        <ul className="mb-4 divide-y divide-slate-100 rounded-xl border border-slate-200">
          {variants.map((v) => {
            const attrs = (v.attributes ?? v.variantAttributes) as
              | Record<string, string>
              | undefined;
            const attrText =
              attrs && typeof attrs === 'object'
                ? Object.entries(attrs)
                    .map(([k, val]) => `${k}: ${val}`)
                    .join(', ')
                : '';
            const variantItemId = String(v.variantItemId ?? v.id);
            return (
              <li
                key={String(v.id)}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm"
              >
                <div>
                  <Link
                    to={`/inventory/${variantItemId}`}
                    className="font-medium text-slate-900 hover:underline"
                  >
                    {String(v.variantName ?? v.name)}
                  </Link>
                  {attrText ? <span className="ml-2 text-slate-500">{attrText}</span> : null}
                </div>
                <span className="font-medium text-slate-700">
                  {formatCurrency(Number(v.retailPrice ?? 0))}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <form
        className="grid gap-3 sm:grid-cols-2"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!variantName.trim()) return;
          const attributes: Record<string, string> = {};
          if (variantAttrKey.trim() && variantAttrValue.trim()) {
            attributes[variantAttrKey.trim()] = variantAttrValue.trim();
          }
          const body: {
            name: string;
            attributes?: Record<string, string>;
            retailPrice?: number;
            skuSuffix?: string;
          } = {
            name: variantName.trim(),
            attributes,
          };
          if (variantPrice.trim() !== '') body.retailPrice = Number(variantPrice) || 0;
          if (variantSkuSuffix.trim()) body.skuSuffix = variantSkuSuffix.trim();
          addVariantMutation.mutate(body);
        }}
      >
        <label className="block sm:col-span-2">
          <span className="text-xs font-medium text-slate-600">Variant name</span>
          <input
            className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
            value={variantName}
            onChange={(e) => setVariantName(e.target.value)}
            placeholder="e.g. iPhone 15 Case — Black"
            required
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Attribute</span>
          <input
            className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
            value={variantAttrKey}
            onChange={(e) => setVariantAttrKey(e.target.value)}
            placeholder="Color"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Value</span>
          <input
            className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
            value={variantAttrValue}
            onChange={(e) => setVariantAttrValue(e.target.value)}
            placeholder="Black"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Retail price</span>
          <input
            type="number"
            min="0"
            step="0.01"
            className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
            value={variantPrice}
            onChange={(e) => setVariantPrice(e.target.value)}
            placeholder="Same as parent if blank"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">SKU suffix</span>
          <input
            className="mt-1 w-full rounded-xl border px-3 py-2 text-sm"
            value={variantSkuSuffix}
            onChange={(e) => setVariantSkuSuffix(e.target.value)}
            placeholder="Optional, e.g. BLK"
          />
        </label>
        <div className="sm:col-span-2">
          <button
            type="submit"
            disabled={addVariantMutation.isPending || !variantName.trim()}
            className="rounded-xl bg-slate-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {addVariantMutation.isPending ? 'Creating…' : 'Create variant'}
          </button>
        </div>
      </form>
    </section>
  );
}
